import React from 'react';
import { X, DollarSign, Clock, PawPrint, AlertCircle, Loader2 } from 'lucide-react';

const QuoteResultModal = ({ show, onClose, quoteData, loading, error }) => {
    if (!show) return null;

    const formatDuration = (minutes) => {
        if (!minutes) return '';
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hrs > 0 && mins > 0) return `${hrs}h ${mins}m`;
        if (hrs > 0) return `${hrs}h`;
        return `${mins}m`;
    };

    const formatPrice = (price) => {
        if (price == null) return '—';
        return `$${Number(price).toFixed(2)}`;
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        Service Quote
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1.5 bg-transparent border border-gray-200 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-grow p-6">
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                            <Loader2 className="w-8 h-8 animate-spin mb-3" />
                            <p className="text-sm">Building quote from service history...</p>
                        </div>
                    )}

                    {error && !loading && (
                        <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-red-800">Quote Failed</p>
                                <p className="text-sm text-red-600 mt-1">{error}</p>
                            </div>
                        </div>
                    )}

                    {quoteData && !loading && !error && (
                        <div className="space-y-6">
                            {/* Source indicator */}
                            {quoteData.source && (
                                <div className="text-xs text-gray-400">
                                    Source: {quoteData.source === 'daysmart_history' ? 'DaySmart Service History' : 'Message Parsing'}
                                </div>
                            )}

                            {/* Per-pet quotes */}
                            {(quoteData.pet_quotes || []).map((petQuote, idx) => (
                                <div key={idx} className="border rounded-lg overflow-hidden">
                                    {/* Pet header */}
                                    <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <PawPrint className="w-4 h-4 text-gray-500" />
                                            <span className="font-semibold text-gray-800">
                                                {petQuote.dog_name || 'Unknown Pet'}
                                            </span>
                                            {petQuote.breed && (
                                                <span className="text-xs text-gray-500">
                                                    ({Array.isArray(petQuote.breed) ? petQuote.breed.join(' / ') : petQuote.breed})
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-gray-500">
                                            {petQuote.weight_lbs && (
                                                <span>{petQuote.weight_lbs} lbs</span>
                                            )}
                                            {petQuote.matched === false && (
                                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                                                    Not matched
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* History summary (existing client) */}
                                    {petQuote.history_summary && (
                                        <div className="px-4 py-2 bg-blue-50 border-b text-xs text-blue-700">
                                            <span className="font-medium">History:</span>{' '}
                                            {petQuote.history_summary.total_past_services} past services
                                            {petQuote.history_summary.coat_type && (
                                                <> · {petQuote.history_summary.coat_type} coat</>
                                            )}
                                            {petQuote.history_summary.typical_services?.length > 0 && (
                                                <> · Typical: {petQuote.history_summary.typical_services.join(', ')}</>
                                            )}
                                            {petQuote.history_summary.has_scissor_history && (
                                                <> · Has scissor history</>
                                            )}
                                        </div>
                                    )}

                                    {/* Note if not matched */}
                                    {petQuote.note && (
                                        <div className="px-4 py-2 bg-yellow-50 border-b text-xs text-yellow-700">
                                            {petQuote.note}
                                        </div>
                                    )}

                                    {/* Quote options */}
                                    {petQuote.quote?.options?.map((option, optIdx) => (
                                        <div key={optIdx} className="px-4 py-3 border-b last:border-b-0">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-medium text-sm text-gray-700">
                                                    {option.category_label || option.category}
                                                </h4>
                                                <div className="flex items-center gap-3 text-sm">
                                                    {option.price_range ? (
                                                        <span className="font-semibold text-green-700">
                                                            {formatPrice(option.price_range.min)} – {formatPrice(option.price_range.max)}
                                                        </span>
                                                    ) : option.total > 0 ? (
                                                        <span className="font-semibold text-green-700">
                                                            {formatPrice(option.total)}
                                                        </span>
                                                    ) : null}
                                                    {option.total_duration_minutes > 0 && (
                                                        <span className="flex items-center gap-1 text-gray-400 text-xs">
                                                            <Clock className="w-3 h-3" />
                                                            {formatDuration(option.total_duration_minutes)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Line items */}
                                            <div className="space-y-1">
                                                {(option.line_items || []).map((item, liIdx) => (
                                                    <div key={liIdx} className="flex items-center justify-between text-xs">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-gray-600 ${item.is_surcharge ? 'italic' : ''}`}>
                                                                {item.service_name}
                                                            </span>
                                                            {item.note && (
                                                                <span className="text-gray-400 text-[10px]">
                                                                    ({item.note})
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-gray-500">
                                                            <span>{formatPrice(item.price)}</span>
                                                            {item.duration_minutes > 0 && (
                                                                <span className="text-gray-300">
                                                                    {formatDuration(item.duration_minutes)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Price range breakdown */}
                                            {option.price_range?.by_group && (
                                                <details className="mt-2">
                                                    <summary className="text-[10px] text-blue-600 hover:text-blue-800 cursor-pointer">
                                                        Price by size group
                                                    </summary>
                                                    <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] text-gray-500">
                                                        {option.price_range.by_group.map((g, gi) => (
                                                            <div key={gi} className="flex justify-between">
                                                                <span>{g.group}</span>
                                                                <span>{formatPrice(g.total)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </details>
                                            )}
                                        </div>
                                    ))}

                                    {/* Ambiguous / scissor info */}
                                    {petQuote.quote?.ambiguous && (
                                        <div className="px-4 py-2 bg-amber-50 text-xs text-amber-700 border-t">
                                            ⚠️ Breed is ambiguous — multiple service categories shown
                                        </div>
                                    )}
                                    {petQuote.quote?.scissor_applied && (
                                        <div className="px-4 py-2 bg-purple-50 text-xs text-purple-700 border-t">
                                            ✂️ Scissor surcharge applied (haircut over 5/8")
                                        </div>
                                    )}
                                    {petQuote.quote?.note && (
                                        <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 border-t">
                                            {petQuote.quote.note}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Message extraction info (for new client path) */}
                            {quoteData.extracted && quoteData.source === 'message_parsing' && (
                                <div className="border rounded-lg p-4 bg-gray-50 text-xs text-gray-600">
                                    <h4 className="font-medium text-gray-700 mb-2">Extracted from message</h4>
                                    {quoteData.extracted.client_name && (
                                        <p><span className="font-medium">Name:</span> {quoteData.extracted.client_name}</p>
                                    )}
                                    {quoteData.extracted.phone && (
                                        <p><span className="font-medium">Phone:</span> {quoteData.extracted.phone}</p>
                                    )}
                                    {quoteData.extracted.notes && (
                                        <p className="mt-1"><span className="font-medium">Notes:</span> {quoteData.extracted.notes}</p>
                                    )}
                                </div>
                            )}

                            {/* No pet quotes */}
                            {(!quoteData.pet_quotes || quoteData.pet_quotes.length === 0) && (
                                <div className="text-center py-8 text-gray-500">
                                    <p className="text-sm">No quotes could be generated.</p>
                                    {quoteData.error && (
                                        <p className="text-xs text-red-500 mt-2">{quoteData.error}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuoteResultModal;
